import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO/SEO';
import MainNav from '../../components/MainNav/MainNav';
import Footer from '../../components/Footer/Footer';
import CoberturaNota from '../../components/CoberturaNota/CoberturaNota';
import './SectoresPage.css';

const SectoresPage = ({ darkMode, toggleDarkMode }) => {
  const sectores = [
    {
      id: 'retail',
      titulo: 'Retail & E-commerce',
      descripcion: 'Soluciones completas para comercio electrónico y retail tradicional',
      icono: '🛒',
      caracteristicas: [
        'Plataformas de e-commerce personalizadas',
        'Integración con sistemas de inventario',
        'Automatización de procesos de venta',
        'Analytics y reporting avanzado'
      ],
      casos: [
        'Tienda online con 50% más conversiones',
        'Sistema de gestión para cadena de 20 tiendas'
      ]
    },
    {
      id: 'manufactura',
      titulo: 'Manufactura & Logística',
      descripcion: 'Optimización de procesos productivos y cadena de suministro',
      icono: '🏭',
      caracteristicas: [
        'Sistemas de gestión de producción',
        'Automatización de procesos industriales',
        'Control de calidad y trazabilidad',
        'Optimización de rutas logísticas'
      ],
      casos: [
        'Reducción del 30% en tiempos de producción',
        'Sistema de trazabilidad completo'
      ]
    },
    {
      id: 'servicios',
      titulo: 'Servicios Profesionales',
      descripcion: 'Herramientas para consultorías, despachos y servicios',
      icono: '💼',
      caracteristicas: [
        'CRM especializado por sector',
        'Automatización de facturación',
        'Gestión de proyectos y recursos',
        'Dashboards de rendimiento'
      ],
      casos: [
        'Consultoría con 40% más eficiencia',
        'Despacho legal completamente digitalizado'
      ]
    },
    {
      id: 'social',
      titulo: 'Social & ONGs',
      descripcion: 'Soluciones tecnológicas para organizaciones sin ánimo de lucro',
      icono: '🤝',
      caracteristicas: [
        'Sistemas de gestión integral',
        'Automatización de procesos administrativos',
        'Plataformas de voluntariado',
        'Analytics de impacto social'
      ],
      casos: [
        'ONG con 80% más donaciones',
        'Fundación con gestión automatizada'
      ]
    },
    {
      id: 'educacion',
      titulo: 'Educación & Formación',
      descripcion: 'Plataformas educativas y gestión académica',
      icono: '🎓',
      caracteristicas: [
        'LMS personalizado',
        'Gestión de estudiantes y cursos',
        'Herramientas de evaluación',
        'Analytics de aprendizaje'
      ],
      casos: [
        'Universidad con 80% de satisfacción',
        'Academia con 200% más estudiantes'
      ]
    },
    {
      id: 'salud',
      titulo: 'Salud & Farmacéutica',
      descripcion: 'Soluciones tecnológicas para el sector sanitario',
      icono: '🏥',
      caracteristicas: [
        'Sistemas de gestión clínica',
        'Cumplimiento normativo sanitario',
        'Gestión de citas y recursos',
        'Analytics de salud poblacional'
      ],
      casos: [
        'Clínica con 60% menos tiempo de espera',
        'Farmacia con gestión automatizada'
      ]
    }
  ];

  return (
    <>
      <SEO 
        title="Soluciones tecnológicas por sector | E-ASY"
        description="Software y consultoría para retail, turismo, manufactura, servicios profesionales, ONG, educación y salud. Proyectos en remoto en toda España, con sede en Tenerife."
        keywords="soluciones tecnológicas retail Canarias, software turismo Tenerife, digitalización manufactura, tecnología servicios profesionales, software ONGs, tecnología educación, software salud"
        canonical="https://e-asy.es/sectores"
      />
      <MainNav darkMode={darkMode} toggleDarkMode={toggleDarkMode} />
      <div className={`sectores-page ${darkMode ? 'dark' : 'light'}`}>
        {/* Hero Section */}
        <section className="sectores-hero">
          <div className="sectores-hero-content">
            <div className="sectores-hero-text">
              <h1 className="sectores-hero-title">Sectores</h1>
              <p className="sectores-hero-subtitle">
                Cada sector tiene sus desafíos únicos. Nuestras soluciones 
                tecnológicas están diseñadas específicamente para resolver 
                los problemas reales de tu industria.
              </p>
              <div className="sectores-hero-stats">
                <div className="hero-stat">
                  <div className="hero-stat-number">6</div>
                  <div className="hero-stat-label">sectores atendidos</div>
                </div>
                <div className="hero-stat">
                  <div className="hero-stat-number">60+</div>
                  <div className="hero-stat-label">proyectos entregados</div>
                </div>
                <div className="hero-stat">
                  <div className="hero-stat-number">95%</div>
                  <div className="hero-stat-label">satisfacción cliente</div>
                </div>
              </div>
            </div>
            <div className="sectores-hero-video">
              <div className="sectores-hero-visual">
                <video 
                  src="/videos/hero-sectores-v1.mp4"
                  alt="Diferentes sectores empresariales representados visualmente"
                  className="sectores-hero-video"
                  autoPlay
                  muted
                  loop
                  playsInline
                />
              </div>
            </div>
          </div>
        </section>

        {/* Sectores Grid */}
        <section className="sectores-grid-section">
          <div className="sectores-grid-content">
            <div className="sectores-grid">
              {sectores.map((sector) => (
                <div key={sector.id} className="sector-card">
                  <div className="sector-header">
                    <h3 className="sector-title">{sector.titulo}</h3>
                    <p className="sector-description">{sector.descripcion}</p>
                  </div>
                  
                  <div className="sector-features">
                    <h4 className="features-title">Soluciones clave</h4>
                    <ul className="features-list">
                      {sector.caracteristicas.map((caracteristica, index) => (
                        <li key={index} className="feature-item">
                          <span className="feature-icon">✓</span>
                          {caracteristica}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="sector-cases">
                    <h4 className="cases-title">Casos de éxito</h4>
                    <ul className="cases-list">
                      {sector.casos.map((caso, index) => (
                        <li key={index} className="case-item">
                          <span className="case-icon">📈</span>
                          {caso}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="sector-cta">
                    {sector.id === 'social' ? (
                      <div className="sector-cta-buttons">
                        <Link to="/tercer-sector" className="sector-button primary">
                          Ver soluciones para ONG →
                        </Link>
                        <Link to="/contacto" className="sector-button secondary">
                          Consultar para {sector.titulo}
                        </Link>
                      </div>
                    ) : (
                      <Link to="/contacto" className="sector-button primary">
                        Consultar para {sector.titulo}
                      </Link>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="sectores-cta-section">
          <div className="sectores-cta-content">
            <h2 className="sectores-cta-title">
              ¿No ves tu sector?
            </h2>
            <p className="sectores-cta-subtitle">
              Nuestra metodología nos permite adaptarnos a cualquier industria. 
              Contáctanos para crear una solución personalizada.
            </p>
            <div className="sectores-cta-buttons">
              <Link to="/contacto" className="cta-button primary">
                Consulta gratuita
              </Link>
              <Link to="/contacto" className="cta-button secondary">
                Ver todos los casos
              </Link>
            </div>
          </div>
        </section>
      </div>
      <CoberturaNota />
      <Footer darkMode={darkMode} toggleDarkMode={toggleDarkMode} />
    </>
  );
};

export default SectoresPage; 